"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePaperIcon = void 0;
var react_1 = require("react");
var util_1 = require("../../util");
var fallbackIcons_1 = require("./fallbackIcons");
var PaperIconCache_1 = require("./PaperIconCache");
var PaperIconConfigContext_1 = require("./PaperIconConfigContext");
/**
 * Hook for loading paper icons
 *
 * @param {PaperIconCache=} props.cache - PaperIconCache object to fetch icons from
 * @param {string=} props.collection - Name of the collection to use (allowed values: "lindua", "linear", "ultimate")
 * @param {string=} props.env - Either "qa", "pprd", or "prod"
 * @param {Function=} props.fetch - Override the window.fetch function to use
 * @param {string|IconData} props.icon - Name of the icon to use (MUST be PascalCased) or the icon data itself
 *                                      (a degenerate case where this hook becomes a passthrough; this allows
 *                                       <NackleIcon> to skip loading an icon if it's already available)
 * @param {Function=} props.onLoad - Callback invoked when an icon loads (asynchronously only)
 * @param {Function=} props.onError - Callback invoked when an icon fails to load (asynchronously only)
 */
function usePaperIcon(props) {
    // Combine default settings (from context) with overrides
    var contextConfig = (0, react_1.useContext)(PaperIconConfigContext_1.paperIconConfigContext);
    var _a = props.cache, cache = _a === void 0 ? contextConfig.cache : _a, _b = props.collection, collection = _b === void 0 ? contextConfig.collection : _b, _c = props.env, env = _c === void 0 ? contextConfig.env : _c, _d = props.fetch, fetch = _d === void 0 ? contextConfig.fetch : _d, icon = props.icon, onLoad = props.onLoad, onError = props.onError;
    var inferredEnv = env === 'infer' ? (0, util_1.inferEnv)() : env;
    var iconDataOrPromise = (0, react_1.useMemo)(function () {
        if (typeof icon === 'string') {
            // User passed a string. Fetch the icon by name.
            return cache.getIcon({
                collection: collection,
                env: inferredEnv,
                fetch: fetch,
                name: icon
            });
        }
        else {
            // User passed the actual icon data they want, so we don't need to fetch anything.
            // This hook is effectively a passthrough.
            // Since getIcon() can return icons synchronously anyway, we can do that as well.
            return icon;
        }
    }, [cache, collection, env, fetch, icon]);
    // This returns either the actual icon (if already available), or a promise that resolves to the icon
    var initialValue = (0, PaperIconCache_1.isLoading)(iconDataOrPromise) ? fallbackIcons_1.LOADING_ICON : iconDataOrPromise;
    var _e = (0, react_1.useState)(initialValue), iconData = _e[0], setIconData = _e[1];
    // If the icon is deferred, then use it when it's loaded
    (0, react_1.useLayoutEffect)(function () {
        if ((0, PaperIconCache_1.isLoading)(iconDataOrPromise)) {
            iconDataOrPromise.then(function (icon) { return setIconData(icon); }, function ( /* error */) { return setIconData(fallbackIcons_1.ERROR_ICON); });
        }
    }, [iconDataOrPromise]);
    // Attach callbacks
    (0, react_1.useLayoutEffect)(function () {
        if (onLoad && (0, PaperIconCache_1.isLoading)(iconDataOrPromise)) {
            iconDataOrPromise.then(onLoad, function ( /* error */) { });
        }
    }, [iconDataOrPromise, onLoad]);
    (0, react_1.useLayoutEffect)(function () {
        if (onError && (0, PaperIconCache_1.isLoading)(iconDataOrPromise)) {
            iconDataOrPromise.catch(onError);
        }
    }, [iconDataOrPromise, onError]);
    return iconData;
}
exports.usePaperIcon = usePaperIcon;
//# sourceMappingURL=usePaperIcon.js.map